/** Data Bank sections and the fixed Pharmacopoeia / Technical Documents trees (from the legacy Drive layout). */

export interface DataBankSection {
  key: string;
  label: string;
  /** Legacy Drive folder name (used to match migrated files). */
  legacyFolder: string;
  needsParty: boolean;
  needsCountry: boolean;
  needsProduct: boolean;
  /** Suggested file types (the upload allow-list still applies). */
  accept: string[];
  description: string;
}

export const DATA_BANK_SECTIONS: DataBankSection[] = [
  {
    key: 'accreditation',
    label: 'Accreditation Certificates',
    legacyFolder: '01_Accreditation_Certificates',
    needsParty: false,
    needsCountry: false,
    needsProduct: false,
    accept: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    description: 'WHO-GMP, ISO, FSSAI, HALAL and other company certificates.',
  },
  {
    key: 'registration',
    label: 'Registration Certificates',
    legacyFolder: '02_Registration_Certificates',
    needsParty: true,
    needsCountry: true,
    needsProduct: false,
    accept: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    description: 'Product registration certificates per exporter and country.',
  },
  {
    key: 'fsc_neuta',
    label: 'FSC Nutra',
    legacyFolder: '03_FSC_Neuta',
    needsParty: true,
    needsCountry: false,
    needsProduct: false,
    accept: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    description: 'Free Sale Certificates for nutraceuticals, per exporter.',
  },
  {
    key: 'fsc_herbal',
    label: 'FSC Herbal',
    legacyFolder: '04_FSC_Herbal',
    needsParty: true,
    needsCountry: false,
    needsProduct: false,
    accept: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    description: 'Free Sale Certificates for herbal products, per exporter.',
  },
  {
    key: 'submitted_dossiers',
    label: 'Submitted Dossiers',
    legacyFolder: '05_Submitted_Dossiers',
    needsParty: true,
    needsCountry: true,
    needsProduct: true,
    accept: ['pdf', 'doc', 'docx', 'zip'],
    description: 'Dossiers as filed with the authority.',
  },
  {
    key: 'submitted_compliance',
    label: 'Submitted Compliance',
    legacyFolder: '06_Submitted_Compliance',
    needsParty: true,
    needsCountry: true,
    needsProduct: true,
    accept: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    description: 'Responses to authority queries.',
  },
  {
    key: 'approved_artworks',
    label: 'Approved Artworks',
    legacyFolder: '07_Approved_Artworks',
    needsParty: true,
    needsCountry: true,
    needsProduct: true,
    accept: ['pdf', 'jpg', 'jpeg', 'png'],
    description: 'Final approved artworks and labels.',
  },
  {
    key: 'submitted_samples',
    label: 'Submitted Samples',
    legacyFolder: '08_Submitted_Samples',
    needsParty: true,
    needsCountry: true,
    needsProduct: true,
    accept: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    description: 'Pack shots of samples sent to the authority.',
  },
  {
    key: 'product_3d_images',
    label: '3D Images of Commercial Products',
    legacyFolder: '09_3D_Images',
    needsParty: true,
    needsCountry: false,
    needsProduct: true,
    accept: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'pdf'],
    description: '3D images of products in their commercial packaging, for the client.',
  },
];

export function dataBankSection(key: string): DataBankSection | undefined {
  return DATA_BANK_SECTIONS.find((s) => s.key === key);
}

// ---- Tree libraries --------------------------------------------------------------------------------

export const LIBRARIES = ['pharmacopoeia', 'technical_documents'] as const;
export type LibraryKey = (typeof LIBRARIES)[number];

export interface LibraryNode {
  /** Slash-separated slugs, unique within the library (e.g. `qc/rm-coa/rm-w-s-coa`). */
  key: string;
  label: string;
  /** Exact legacy Drive folder name when it differs from the label. */
  legacyLabel?: string;
  children?: LibraryNode[];
}

function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function node(label: string, children?: (string | LibraryNode)[], legacyLabel?: string): LibraryNode {
  return { key: slug(label), label, ...(legacyLabel ? { legacyLabel } : {}), ...(children ? { children: children.map((c) => (typeof c === 'string' ? node(c) : c)) } : {}) };
}

/** Prefixes every descendant key with its ancestors' keys. */
function withPaths(nodes: LibraryNode[], prefix = ''): LibraryNode[] {
  return nodes.map((n) => {
    const key = prefix ? `${prefix}/${n.key}` : n.key;
    return { ...n, key, ...(n.children ? { children: withPaths(n.children, key) } : {}) };
  });
}

const copies = (name: string, extra: string[] = []) => [`All ${name} Soft Copies`, `All ${name} JPG Copies`, `All ${name} PDF Copies`, ...extra];
const fnd = ['BOM', 'MFR', 'PDR', 'QNQ'];
const dosage = ['Tablet', 'Capsule', 'Sachet'];
const batchDocs = ['BMR BPR', 'Master BMR BPR', 'Filled BMR BPR', 'PVP PVR'];

export const TECHNICAL_DOCUMENTS_TREE: LibraryNode[] = withPaths([
  node('QA', ['SOP', 'QMS Documents']),
  node('QC', [
    node('RMS', copies('RMS')),
    node('RM STP', copies('RM STP')),
    node('RM COA', copies('RM COA', ['RM Vendor COA', 'RM W/S COA', 'RM R/S COA'])),
    node('PMS', copies('PMS')),
    node('PM STP', copies('PM STP')),
    node('PM COA', copies('PM COA', ['PM Vendor COA'])),
    node('FPS', copies('FPS')),
    node('FP STP', copies('FP STP')),
    node('FP COA', copies('FP COA', ['NABL COA'])),
  ]),
  node('FND', fnd),
  node('FND (SGC)', fnd),
  node('FND (Cosmetics)', fnd, 'FND(Cosmetics)'),
  node('Production', [
    node('OSD', [node('Master BMR BPR', dosage), node('Filled BMR BPR', dosage), node('PVP PVR', dosage)]),
    node('Liquid', batchDocs),
    node('Softgel', batchDocs),
    node('Cosmetics', batchDocs),
  ]),
]);

export const PHARMACOPOEIA_TREE: LibraryNode[] = withPaths([
  node('All Pharmacopoeias', ['IP', 'BP', 'USP', 'Ayurvedic Pharmacopoeia of India', 'Martindale']),
  node('All Books'),
]);

/** Country guidelines/checklists live under `countries/<country>/<category>/<leaf>`. */
export const PHARMA_COUNTRY_CATEGORIES: LibraryNode[] = [
  { key: 'herbal', label: 'Herbal' },
  { key: 'nutraceutical', label: 'Nutraceutical', legacyLabel: 'Neutra' },
  { key: 'cosmetics', label: 'Cosmetics' },
];
export const PHARMA_COUNTRY_LEAVES: LibraryNode[] = [
  { key: 'guidelines', label: 'Guidelines' },
  { key: 'checklist', label: 'Checklist' },
];

export const LIBRARY_LABELS: Record<LibraryKey, string> = {
  pharmacopoeia: 'Pharmacopoeia & Guidelines',
  technical_documents: 'Technical Documents',
};

export function libraryTree(library: LibraryKey): LibraryNode[] {
  return library === 'pharmacopoeia' ? PHARMACOPOEIA_TREE : TECHNICAL_DOCUMENTS_TREE;
}

export interface LibraryLeaf {
  key: string;
  path: string[];
  legacyPath: string[];
}

/** Every leaf of a fixed tree, with its label path (and the legacy Drive folder names). */
export function libraryLeaves(nodes: LibraryNode[], path: string[] = [], legacyPath: string[] = []): LibraryLeaf[] {
  return nodes.flatMap((n) =>
    n.children
      ? libraryLeaves(n.children, [...path, n.label], [...legacyPath, n.legacyLabel ?? n.label])
      : [{ key: n.key, path: [...path, n.label], legacyPath: [...legacyPath, n.legacyLabel ?? n.label] }],
  );
}

export function countrySlug(country: string): string {
  return slug(country) || 'country';
}

/**
 * Resolves a node key to its label path, or null when it is not a leaf where files may live.
 * Pharmacopoeia country leaves are `countries/<country-slug>/<category>/<leaf>`; the country label is passed separately.
 */
export function resolveLibraryLeaf(library: LibraryKey, key: string, countryName?: string | null): string[] | null {
  const leaf = libraryLeaves(libraryTree(library)).find((l) => l.key === key);
  if (leaf) return leaf.path;
  if (library !== 'pharmacopoeia' || !countryName) return null;
  const [root, country, category, target, ...rest] = key.split('/');
  const cat = PHARMA_COUNTRY_CATEGORIES.find((c) => c.key === category);
  const lf = PHARMA_COUNTRY_LEAVES.find((l) => l.key === target);
  if (root !== 'countries' || rest.length || !cat || !lf || country !== countrySlug(countryName)) return null;
  return ['All Countries Guidelines And Checklist', countryName, cat.label, lf.label];
}

export function pharmaCountryKey(country: string, category: string, leaf: string): string {
  return `countries/${countrySlug(country)}/${category}/${leaf}`;
}
